import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import type { UserPrimaryRole } from '@/types'
import { LoadingScreen } from '@/components/ui/LoadingScreen'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole: UserPrimaryRole
}

// Map each role to its home portal path
const ROLE_HOME: Record<UserPrimaryRole, string> = {
  school_admin: '/app/school',
  instructor: '/app/instructor',
  student: '/app/student',
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { session, profile, loading, profileLoading } = useAuth()
  const location = useLocation()

  // While initial auth state resolves, show a blank loading screen
  if (loading || profileLoading) {
    return <LoadingScreen />
  }

  // Not authenticated — redirect to login, preserving the intended destination
  if (!session) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
  }

  // Authenticated but no profile yet (e.g. brand-new Google user who hasn't selected a role)
  if (!profile) {
    return <Navigate to='/auth/select-role' replace />
  }

  // Profile exists but role is not set (same case — new Google OAuth user)
  if (!profile.primary_role) {
    return <Navigate to='/auth/select-role' replace />
  }

  // Wrong role — redirect to the user's actual portal instead of showing an error
  if (profile.primary_role !== requiredRole) {
    const correctHome = ROLE_HOME[profile.primary_role]
    return <Navigate to={correctHome} replace />
  }

  // Suspended account
  if (profile.account_status === 'suspended') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#FAF6EF', padding: '24px',
      }}>
        <div style={{
          maxWidth: 440, background: '#fff', borderRadius: 16,
          padding: '32px 28px', border: '1px solid rgba(224,102,59,0.3)',
          textAlign: 'center',
        }}>
          <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 10 }}>
            Account suspended
          </div>
          <div style={{ font: '400 14px Inter,sans-serif', color: 'rgba(27,42,74,0.65)' }}>
            Your account has been suspended. Please contact support.
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
